import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserCircle, SignOut, Lifebuoy, ArrowRight } from '@phosphor-icons/react';
import API from '../lib/api';
import { useAuth } from '../lib/auth';

const STATUS_LABELS = {
  pending_payment: 'Payment',
  pending_partner: 'File in review',
  unassigned: 'Unassigned',
  accepted: 'Accepted',
  printing: 'Printing',
  printed: 'Printed',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
};

const Profile = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    if (user?.role === 'customer') API.get('/orders').then(r => setOrders(r.data)).catch(() => {});
  }, [user]);

  const stats = {
    total: orders.length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    active: orders.filter(o => !['delivered', 'unassigned'].includes(o.status)).length,
  };

  const initial = (user?.name || 'U').charAt(0).toUpperCase();

  return (
    <div className="p-6">
      <div className="reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Profile</div>
        <h1 className="font-display text-[26px] mt-2 leading-tight">Hey, {user?.name?.split(' ')[0]}.</h1>
      </div>

      <div className="mt-5 pk-card p-4 flex items-center gap-4">
        <div className="w-14 h-14 border-2 border-black font-display text-[26px] flex items-center justify-center" style={{ background: 'var(--pk-yellow)' }}>{initial}</div>
        <div className="flex-1">
          <div className="font-bold text-[15px]">{user?.name}</div>
          <div className="text-[11px] text-neutral-500">{user?.email}</div>
          <div className="text-[10px] tracking-[0.22em] uppercase mt-1">{user?.role}</div>
        </div>
      </div>

      {user?.role === 'customer' && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[['Total', stats.total], ['Delivered', stats.delivered], ['Active', stats.active]].map(([l, v]) => (
            <div key={l} className="pk-card p-3 text-center">
              <div className="font-display text-[22px] font-mono">{v}</div>
              <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      )}

      {user?.role === 'customer' && (
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.24em] uppercase font-bold mb-2">Order history</div>
          <div className="space-y-2">
            {orders.slice(0, 8).map(o => (
              <Link key={o.order_id} to={`/orders/${o.order_id}`} className="pk-card p-3 flex items-center gap-2" data-testid={`profile-order-${o.order_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">#{o.order_id.slice(-8)}</div>
                  <div className="font-bold text-[12px] mt-0.5 truncate">{o.items[0]?.product_name}{o.items.length > 1 ? ` +${o.items.length - 1}` : ''}</div>
                </div>
                <div className="pk-chip active text-[9px] shrink-0 justify-center text-center whitespace-nowrap" style={{ minWidth: 96 }}>{STATUS_LABELS[o.status]}</div>
                <ArrowRight size={14} weight="bold" className="shrink-0 text-neutral-400" />
              </Link>
            ))}
            {orders.length === 0 && <div className="text-[12px] text-neutral-400 py-4 text-center">No orders yet.</div>}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        <Link to="/support" className="pk-card p-4 flex items-center gap-3" data-testid="profile-support"><Lifebuoy size={20} weight="duotone"/> <div className="flex-1 font-bold text-[13px]">Support</div><ArrowRight size={14}/></Link>
        {user?.role === 'partner' && (
          <>
            <Link to="/partner" className="pk-card p-4 flex items-center gap-3"><UserCircle size={20} weight="duotone"/> <div className="flex-1 font-bold text-[13px]">Partner dashboard</div><ArrowRight size={14}/></Link>
            <Link to="/partner/earnings" className="pk-card p-4 flex items-center gap-3"><UserCircle size={20} weight="duotone"/> <div className="flex-1 font-bold text-[13px]">Earnings & cashout</div><ArrowRight size={14}/></Link>
          </>
        )}
        {user?.role === 'admin' && (
          <Link to="/admin" className="pk-card p-4 flex items-center gap-3"><UserCircle size={20} weight="duotone"/> <div className="flex-1 font-bold text-[13px]">Admin dashboard</div><ArrowRight size={14}/></Link>
        )}
        <button onClick={async () => { await logout(); nav('/'); }} className="pk-card p-4 flex items-center gap-3 w-full text-left" data-testid="logout-btn">
          <SignOut size={20}/> <div className="flex-1 font-bold text-[13px]">Log out</div>
        </button>
      </div>
    </div>
  );
};

export default Profile;
